/**
 * ThriveIdCell — a Thrive CONTACT id as a deep link into the tenant web app,
 * optionally with Thrive's own photo of that contact to its left.
 *
 * Extracted from People Lists' `ContactsTable` (the `thrive-avatar-cell`,
 * 2026-10-07, epic `2026-10-06-search-candidates-reports`, sub-task 05) so the
 * Candidates tab renders the SAME link instead of a third copy. The two
 * callers differ in one thing, and that is the one prop that changes the
 * output:
 *
 *   - People Lists shows the photo (`showAvatar`): THRIVE's picture sits left
 *     of THRIVE's id so the eye can compare it against the import row's own
 *     avatar in the name cell — left = what we imported, right = who Thrive
 *     thinks that is. The slot renders on every linked row, photo or not, so
 *     the column stays vertically aligned; `Avatar` collapses the no-photo
 *     cases to one quiet silhouette, and `name` is deliberately NOT passed —
 *     initials from the IMPORT row's name beside THRIVE's id would assert the
 *     two are the same person, precisely the claim a W2 identity mismatch
 *     exists to question.
 *   - The Candidates tab hides it: there the row IS the Thrive contact, its
 *     photo belongs beside the name, and a second copy of it next to the id
 *     would say nothing the first did not.
 *
 * The test ids are props with People Lists' values as defaults, so that
 * table's tests and specs (`thrive-avatar-cell`, `thrive-id-link`) keep
 * passing unchanged — they are the proof the extraction changed nothing — and
 * the Candidates tab names its own (`candidate-thrive-id`).
 *
 * `data-testid` is on the wrapper, not the `Avatar`: the primitive owns its own
 * test id, which VARIES by rendered state (`avatar-image` vs
 * `avatar-placeholder`) and is also used by the name cell's avatar in the same
 * row. Scoping a query to this wrapper is what lets a test assert WHICH of the
 * two it found.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import { Avatar } from "@nu-assist/ui";

import { thriveContactUrl } from "../lib/thriveLinks";

export interface ThriveIdCellProps {
  /** Thrive's contact id — the link text and the path segment. */
  contactId: string;
  /** Render Thrive's photo of the contact left of the id (People Lists). */
  showAvatar: boolean;
  /** The photo's URL when `showAvatar`; absent or failed → silhouette. */
  avatarSrc?: string | null;
  /** The wrapper's test id. Default: People Lists' `thrive-avatar-cell`. */
  cellTestId?: string;
  /** The link's test id. Default: People Lists' `thrive-id-link`. */
  linkTestId?: string;
}

export function ThriveIdCell({
  contactId,
  showAvatar,
  avatarSrc,
  cellTestId = "thrive-avatar-cell",
  linkTestId = "thrive-id-link",
}: ThriveIdCellProps) {
  return (
    <div data-testid={cellTestId} className="flex items-center gap-2">
      {showAvatar ? (
        // `size="lg"` deliberately matches the name cell's avatar in People
        // Lists: the two are peers, and an earlier `sm` read as a hierarchy.
        // Passed explicitly (not left to Avatar's default) so both sides state
        // the same intent; `ContactsTable.test.tsx` asserts parity without
        // pinning the pixel value.
        <Avatar src={avatarSrc} size="lg" />
      ) : null}
      <a
        data-testid={linkTestId}
        href={thriveContactUrl(contactId)}
        target="_blank"
        rel="noreferrer"
        className="text-accent hover:text-accent-hover hover:underline"
      >
        {contactId}
      </a>
    </div>
  );
}
